import React, { forwardRef, useContext, useEffect, useRef } from 'react'

import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FieldLabel } from '@cash-design-system/react'
import { useMutationSetPassword } from '@cash-web/shared-data-access-api/react-query/useMutationSetPassword'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { FormInput, PasswordInput, PasswordInputLegacy } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { catchPromise } from '../../util/catchPromise'
import { BaseBlocker, SubmitButton } from '../BaseBlocker/BaseBlocker'

type PasswordCreationForm = {
  'new-password': string
}

const NewPasswordInput = forwardRef<HTMLInputElement, React.ComponentProps<typeof PasswordInput>>((props, ref) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Set the non-standard "passwordrules" attribute against React's wishes.
    // Apple recommends using this attribute https://developer.apple.com/password-rules/.
    containerRef.current?.querySelector('#new-password')?.setAttribute('passwordrules', 'minlength: 8; maxlength: 64;')
  })

  return (
    <div style={{ display: 'contents' }} ref={containerRef}>
      <PasswordInputLegacy {...props} ref={ref} />
    </div>
  )
})

export const BlockerPasswordCreation = () => {
  const { t } = useTranslation()
  const { setError, error } = useContext(ErrorContext)
  const flow = useFlowContext()
  const clientScenario = flow.active ? flow.active.clientScenario : undefined
  const flowToken = flow.active ? flow.active.flowToken : undefined
  const { mutateAsync: setPasscode } = useMutationSetPassword({ options: { clientScenario, flowToken } })
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const form = useForm<PasswordCreationForm>({
    defaultValues: {
      'new-password': '',
    },
  })

  const password = useWatch({ name: 'new-password', control: form.control })

  const handleSubmit = async (values: PasswordCreationForm) => {
    const response = await catchPromise(setPasscode({ new_password: values['new-password'] }))
    const errorMessage = response.password_error_message ?? response.response_context?.dialog_message
    if (errorMessage) {
      setError(errorMessage)
    } else {
      flow.updateWithResponse({ response: Promise.resolve(response), queriesToInvalidate: [] })
    }
  }

  return (
    <BaseBlocker
      mainText={t('Create a password', { context: 'title' })}
      secondaryText={t(
        'This helps keep your tax info secure. You’ll only use this password for Cash App Taxes, not to log into Cash App.',
        { context: 'subtitle' }
      )}
      footer={null}
      onSubmit={handleSubmit}
      form={form}
      buttons={[<SubmitButton key="submit-password-btn" disabled={!password} />]}
    >
      {isArcadeMigrationEnabled ? (
        <PasswordInput autoFocus={true} isNewPassword={true} />
      ) : (
        <FieldLabel label={t('Password', { context: 'label' })}>
          <FormInput
            autoFocus={true}
            customInput={NewPasswordInput}
            placeholder={t('Enter password', { context: 'placeholder' })}
            name="new-password"
            autoComplete="new-password"
            isInvalid={Boolean(error)}
          />
        </FieldLabel>
      )}
    </BaseBlocker>
  )
}
